import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export interface PlayingVideo {
  /** Ready-to-use embed URL from buildYoutubeEmbedUrl(). */
  embedUrl: string;
  /** The original link, for the escape hatch out to YouTube. */
  watchUrl: string;
  title: string;
  author?: string;
}

interface VideoModalProps {
  /** The video to play, or null when nothing is playing. */
  video: PlayingVideo | null;
  onClose: () => void;
}

/**
 * Plays a video over the page instead of sending the visitor to a new tab.
 *
 * Built on the same headless-ui Dialog as the rest of the app's modals but kept
 * separate from `Modal`: that one is a confirm dialog — narrow, with a required
 * confirm/cancel footer — and a player wants the width, a 16:9 frame and no buttons.
 *
 * The iframe is unmounted with the dialog, which is what actually stops playback;
 * hiding it would leave audio running behind a closed modal.
 */
export default function VideoModal({ video, onClose }: VideoModalProps) {
  const closeButtonRef = useRef(null);

  // Keep rendering the last video through the closing animation. Reading `video`
  // directly would blank the frame the instant it is cleared, so the modal would
  // fade out empty.
  const [shown, setShown] = useState<PlayingVideo | null>(video);
  useEffect(() => {
    if (video) setShown(video);
  }, [video]);

  const current = video ?? shown;

  return (
    <Transition.Root show={video !== null} as={Fragment} afterLeave={() => setShown(null)}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={closeButtonRef}
        onClose={onClose}
        // The card this renders from may itself be clickable. A portal escapes the
        // DOM but not React's event tree, so without this a click on the player
        // would bubble back up and re-open the content.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-80 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl transform transition-all">
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  aria-label="Close video"
                  className="absolute -top-9 right-0 rounded-full p-1 text-white/80 transition hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <XMarkIcon className="h-7 w-7" aria-hidden="true" />
                </button>

                {current && (
                  <>
                    {/* 16:9 frame, capped by height so a landscape phone still
                        shows the whole player and its controls. */}
                    <div className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl">
                      <div className="w-full pt-[56.25%]" />
                      <iframe
                        key={current.embedUrl}
                        src={current.embedUrl}
                        title={current.title}
                        className="absolute inset-0 h-full w-full max-h-[80vh]"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </div>

                    <div className="mt-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                      <div className="min-w-0">
                        <Dialog.Title className="truncate text-sm sm:text-base font-bold text-white">
                          {current.title}
                        </Dialog.Title>
                        {current.author && (
                          <p className="truncate text-xs sm:text-sm text-white/70">
                            {current.author}
                          </p>
                        )}
                      </div>
                      <a
                        href={current.watchUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-xs sm:text-sm font-semibold text-white/80 underline underline-offset-2 hover:text-white"
                      >
                        Watch on YouTube ↗
                      </a>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
